import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { TemplateThumbnail } from '../components/TemplateThumbnail';
import { NewsletterThumbnail } from '../components/NewsletterThumbnail';
import { ArrowRight, Sparkles, Lock, Loader2 } from 'lucide-react';

const DEMO_PROPERTY = {
  address: '2847 Sunset Blvd, Beverly Hills, CA 90210',
  price: '$1,495,000',
  beds: '4',
  baths: '3',
  sqft: '2,450',
  property_summary: 'Stunning modern home with panoramic city views, gourmet kitchen, and resort-style pool. A true entertainer\'s dream.',
  key_features: ['Pool', 'Smart Home', 'Gourmet Kitchen', 'City Views'],
  main_image_url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
  photos: [
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80',
  ],
};

const FORMAT_LABELS = {
  post: 'Instagram Post',
  story: 'Instagram Story',
  reel: 'Video Reel',
  carousel: 'Carousel',
  flyer: 'Print Flyer',
  newsletter: 'Email Newsletter',
};

export const DemoPreviewPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/templates/showcase/public')
      .then(res => {
        if (cancelled) return;
        setTemplates(res.data?.templates || []);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const active = templates[activeIdx];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-violet-50/30">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="demo-logo">
            RealProp
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold tracking-tight" data-testid="demo-mode-badge">
              <Sparkles className="h-3 w-3" />
              Demo Mode — Read Only
            </span>
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="demo-login-btn">
              Sign In
            </Button>
            <Button onClick={() => navigate('/signup')} className="bg-black text-white hover:bg-gray-900 gap-1.5" data-testid="demo-signup-btn">
              Get Started Free <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="demo-title">
            See RealProp in Action
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Real templates filled with sample property data. Pick a format below to see how your listings will look.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20 text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
            No demo templates available yet. <Link to="/signup" className="text-violet-600 underline">Sign up</Link> to see all templates.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,2fr] gap-8">
            {/* Format selector */}
            <div className="space-y-2" data-testid="demo-format-list">
              {templates.map((t, idx) => (
                <button
                  key={t.template_id || idx}
                  onClick={() => setActiveIdx(idx)}
                  className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
                    idx === activeIdx
                      ? 'border-black bg-white shadow-[0_4px_16px_rgba(0,0,0,0.08)] -translate-y-0.5'
                      : 'border-gray-200 bg-white/60 hover:bg-white hover:border-gray-300'
                  }`}
                  data-testid={`demo-format-${t.type}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>{FORMAT_LABELS[t.type] || t.type}</p>
                      <p className="text-sm font-semibold tracking-tight mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name}</p>
                    </div>
                    {idx === activeIdx && <ArrowRight className="h-4 w-4" />}
                  </div>
                </button>
              ))}
              {/* Locked CTA tile */}
              <div className="p-5 rounded-xl border border-dashed border-violet-300 bg-violet-50/50 mt-4" data-testid="demo-locked-cta">
                <Lock className="h-5 w-5 text-violet-500 mb-2" />
                <p className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Unlock hundreds of curated templates</p>
                <p className="text-xs text-gray-500 mt-1 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Posts, Reels, Carousels, Flyers, Newsletters — all customizable with your branding.</p>
                <Button onClick={() => navigate('/signup')} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="demo-unlock-btn">
                  Unlock All Templates
                </Button>
              </div>
            </div>

            {/* Active preview */}
            <div className="flex items-start justify-center" data-testid="demo-preview-canvas">
              {active && (
                <div className="bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.08)] p-6 max-w-[600px] w-full">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>{FORMAT_LABELS[active.type] || active.type}</p>
                      <p className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="demo-active-name">{active.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center bg-gray-50 rounded-xl overflow-hidden p-4">
                    {active.type === 'newsletter' ? (
                      <NewsletterThumbnail template={active} width={480} />
                    ) : (
                      <TemplateThumbnail canvasData={active.canvas_data} propertyData={DEMO_PROPERTY} width={480} />
                    )}
                  </div>
                  <div className="mt-5 p-4 rounded-xl bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-200/60 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Like what you see?</p>
                      <p className="text-xs text-gray-600 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Create unlimited posts with your branding in under 2 minutes.</p>
                    </div>
                    <Button onClick={() => navigate('/signup')} className="bg-black text-white hover:bg-gray-900 gap-1.5 flex-shrink-0" data-testid="demo-cta-signup">
                      Start Free <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DemoPreviewPage;
